import React, { useCallback, useEffect, useState } from 'react';
import { API_BASE_URL } from '../../config';
import { getBearerAuthHeaders } from '../../utils/authFetchHeaders';
import './CompaniesPanel.css';

const BASE = `${API_BASE_URL}/api/entities/admin`;

async function call(url, options = {}) {
  const res = await fetch(url, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...getBearerAuthHeaders(), ...(options.headers || {}) },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.success === false) throw new Error(data.error || 'Request failed');
  return data;
}

/**
 * Admin → Brands → Companies. Groups brands owned by one business. A company's
 * staff (Support in Trade Watcher, Max and up in My Portfolio) see people across
 * all of its brands, and nobody outside it. A brand with no company stands alone.
 */
export default function CompaniesPanel({ entities = [], onChanged }) {
  const [companies, setCompanies] = useState([]);
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null); // { slug, name }

  const load = useCallback(async () => {
    try {
      const data = await call(`${BASE}/companies`);
      setCompanies(data.companies || []);
      setError('');
    } catch (e) {
      setError(e.message);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const run = async (key, fn) => {
    setBusy(key);
    setError('');
    try {
      await fn();
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy('');
    }
  };

  const create = () => run('create', async () => {
    await call(`${BASE}/companies`, { method: 'POST', body: JSON.stringify({ displayName: newName }) });
    setNewName('');
  });

  const rename = () => run(`rename:${editing.slug}`, async () => {
    await call(`${BASE}/companies/${encodeURIComponent(editing.slug)}`, {
      method: 'PUT',
      body: JSON.stringify({ displayName: editing.name }),
    });
    setEditing(null);
  });

  const remove = (company) => {
    const count = company.brandSlugs.length;
    const ok = window.confirm(
      `Delete the company "${company.displayName}"?`
      + (count ? ` Its ${count} brand${count === 1 ? '' : 's'} will stand alone, so their staff only see their own brand.` : ''),
    );
    if (!ok) return;
    run(`delete:${company.slug}`, () => call(`${BASE}/companies/${encodeURIComponent(company.slug)}`, { method: 'DELETE' }));
  };

  const assign = (entitySlug, companySlug) => run(`assign:${entitySlug}`, () => call(
    `${BASE}/${encodeURIComponent(entitySlug)}`,
    { method: 'PUT', body: JSON.stringify({ companySlug: companySlug || null }) },
  ));

  const companyOf = (entity) => entity.companySlug
    || companies.find((c) => c.brandSlugs.includes(entity.slug))?.slug
    || '';

  return (
    <section className="ae-card cp-card">
      <h2 className="ae-card-title">Companies</h2>
      <p className="ae-sub">
        Group brands owned by one business. That company&apos;s Support team (Trade Watcher) and Max-and-up
        staff (My Portfolio) see people across all of its brands, and nobody outside it. A brand with no
        company stands alone. Platform Admin always sees everyone.
      </p>
      {error ? <div className="ae-error" role="alert">{error}</div> : null}

      <div className="cp-grid">
        <div className="cp-col">
          <h3 className="cp-subhead">Brand → company</h3>
          <ul className="cp-list">
            {entities.map((e) => (
              <li key={e.slug} className="cp-row">
                <span className="cp-brand">{e.displayName}</span>
                <select
                  className="cp-select"
                  aria-label={`Company for ${e.displayName}`}
                  value={companyOf(e)}
                  disabled={busy === `assign:${e.slug}`}
                  onChange={(ev) => assign(e.slug, ev.target.value)}
                >
                  <option value="">No company (stands alone)</option>
                  {companies.map((c) => (
                    <option key={c.slug} value={c.slug}>{c.displayName}</option>
                  ))}
                </select>
              </li>
            ))}
          </ul>
        </div>

        <div className="cp-col">
          <h3 className="cp-subhead">Companies</h3>
          {companies.length === 0 ? <p className="cp-empty">No companies yet.</p> : null}
          <ul className="cp-list">
            {companies.map((c) => (
              <li key={c.slug} className="cp-row cp-company">
                {editing?.slug === c.slug ? (
                  <>
                    <input
                      className="cp-input"
                      value={editing.name}
                      maxLength={80}
                      onChange={(ev) => setEditing({ ...editing, name: ev.target.value })}
                      aria-label="Company name"
                    />
                    <button type="button" className="ae-btn ae-btn-primary" onClick={rename} disabled={!editing.name.trim() || Boolean(busy)}>Save</button>
                    <button type="button" className="ae-btn" onClick={() => setEditing(null)}>Cancel</button>
                  </>
                ) : (
                  <>
                    <span className="cp-company-name">
                      {c.displayName}
                      <small>
                        {c.brandSlugs.length
                          ? c.brandSlugs.map((slug) => entities.find((e) => e.slug === slug)?.displayName || slug).join(', ')
                          : 'No brands yet'}
                      </small>
                    </span>
                    <button type="button" className="ae-btn" onClick={() => setEditing({ slug: c.slug, name: c.displayName })}>Rename</button>
                    <button type="button" className="ae-btn" onClick={() => remove(c)} disabled={busy === `delete:${c.slug}`}>Delete</button>
                  </>
                )}
              </li>
            ))}
          </ul>
          <div className="cp-add">
            <input
              className="cp-input"
              placeholder="New company name"
              value={newName}
              maxLength={80}
              onChange={(ev) => setNewName(ev.target.value)}
              onKeyDown={(ev) => { if (ev.key === 'Enter' && newName.trim()) create(); }}
              aria-label="New company name"
            />
            <button type="button" className="ae-btn ae-btn-primary" onClick={create} disabled={!newName.trim() || busy === 'create'}>
              {busy === 'create' ? 'Adding…' : 'Add company'}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
